import React, { useState, useEffect } from 'react';
import dbService from '../../../services/dbService';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Tooltip, OverlayTrigger } from 'react-bootstrap';
import PageContainer from '../../layout/PageContainer'; // <-- IMPORT

const AlertQueue = () => {
    // --- STATE MANAGEMENT ---
    const [alerts, setAlerts] = useState([]);
    const { user } = useAuth();
    const navigate = useNavigate();

    // --- DATA FETCHING & PRIORITIZATION ---
    const fetchAlerts = async () => {
        const allAlerts = await dbService.getAll('alerts');
        const newAlerts = allAlerts.filter(a => a.status === 'New');
        // This sort is the core of real-time prioritization
        newAlerts.sort((a, b) => b.riskScore - a.riskScore);
        setAlerts(newAlerts);
    };

    useEffect(() => {
        fetchAlerts();
    }, []);

    // --- HANDLERS ---
    const handlePromoteToCase = async (alert) => {
        const newCase = {
            customerId: alert.customerId,
            customerName: alert.customerName,
            risk: alert.riskScore > 75 ? 'High' : 'Medium', // Derive case risk from alert score
            status: 'Open',
            assignedTo: 'Unassigned',
            openDate: new Date().toISOString().split('T')[0],
            notes: [{ user: 'System', text: `Case created from Alert #${alert.id} (${alert.ruleName}).`, timestamp: new Date().toISOString() }],
            auditTrail: [{ user: 'System', action: `Case Created from Alert #${alert.id}`, timestamp: new Date().toISOString() }],
            transactions: alert.transactions
        };

        const newCaseId = await dbService.add('cases', newCase);
        await dbService.update('alerts', { ...alert, status: 'Case Created' });
        await dbService.addLog({ user: user.name, action: 'Promoted Alert to Case', details: `Alert ID: ${alert.id} -> Case ID: ${newCaseId}` });
        
        toast.success(`Alert promoted to new Case #${newCaseId}`);
        navigate(`/case-management/${newCaseId}`);
    };

    const handleDismiss = async (alert) => {
        await dbService.update('alerts', { ...alert, status: 'Dismissed' });
        await dbService.addLog({ user: user.name, action: 'Dismissed Alert', details: `Alert ID: ${alert.id} marked as False Positive.` });
        toast.warn(`Alert #${alert.id} dismissed.`);
        fetchAlerts();
    };
    
    // --- RENDER ---
    return (
        <PageContainer title="Alert Queue">
            <p>Review new alerts generated by the transaction monitoring engine, prioritized by a combined risk score from the Rule Engine and AI/ML model.</p>
            <div className="card mt-4">
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead>
                                <tr>
                                    <th style={{width: '15%'}}>Priority Score</th>
                                    <th>Customer</th>
                                    <th>Rule Triggered</th>
                                    <th>Date</th>
                                    <th style={{width: '20%'}}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {alerts.length > 0 ? alerts.map(alert => (
                                    <tr key={alert.id} className={alert.riskScore > 75 ? 'table-danger' : ''}>
                                        <td>
                                            <OverlayTrigger overlay={<Tooltip>This is a combined score from the rule, AI model, and transaction value.</Tooltip>}>
                                                <span className="badge bg-danger fs-5">{alert.riskScore}</span>
                                            </OverlayTrigger>
                                        </td>
                                        <td><Link to={`/kyc-profile/${alert.customerId}`}>{alert.customerName}</Link></td>
                                        <td>{alert.ruleName}</td>
                                        <td>{new Date(alert.date).toLocaleString()}</td>
                                        <td>
                                            <Button variant="primary" size="sm" onClick={() => handlePromoteToCase(alert)}>Create Case</Button>
                                            <Button variant="secondary" size="sm" className="ms-2" onClick={() => handleDismiss(alert)}>Dismiss</Button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="5" className="text-center text-muted p-4">No new alerts. Go to the Transaction Generator to create some.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </PageContainer>
    );
};

export default AlertQueue;